import React, { createContext, useContext, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { ZinSnackbar, ZinButton } from '@tnl/zin-react'
import { useBreakpoint } from '@shared/hooks'

export interface SnackbarAction {
  label: string
  icon?: string
  variant?: 'link'
  onClick?: () => void
}

interface SnackbarMessage {
  message: string | React.ReactNode
  type?: 'success' | 'error' | 'warning' | 'info'
  duration?: number
  persistent?: boolean
  onUndo?: () => void
  primaryAction?: SnackbarAction
  secondaryAction?: SnackbarAction
  onClose?: (forced: boolean) => void
  actions?: SnackbarAction[]
}

interface SnackbarContextType {
  showSnackbar: (message: SnackbarMessage) => void
  hideSnackbar: () => void
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined)

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [snackbar, setSnackbar] = useState<SnackbarMessage | null>(null)
  const { isMobile } = useBreakpoint()

  const showSnackbar = useCallback((message: SnackbarMessage) => {
    setSnackbar(message)
  }, [])

  const showSnackbarWithActions = useCallback((message: SnackbarMessage, primaryAction: SnackbarAction, secondaryAction?: SnackbarAction) => {
    setSnackbar({
      ...message,
      primaryAction,
      secondaryAction,
    })
  })

  const hideSnackbar = useCallback(() => {
    setSnackbar(null)
  }, [])

  const handleDismiss = useCallback(
    (forced: boolean) => {
      if (snackbar?.onClose) {
        snackbar.onClose(forced)
      }
      hideSnackbar()
    },
    [snackbar, hideSnackbar],
  )

  const snackbarNode = snackbar ? (
    <div className={`snackbar-portal fixed bottom-8 left-1/2 transform -translate-x-1/2 ${isMobile ? 'snackbar-mobile' : ''}`}>
      <ZinSnackbar
        countdown
        persistent={snackbar.onUndo ? undefined : snackbar.persistent}
        duration={snackbar.duration || 3}
        type={snackbar.type || 'info'}
        onClose={() => handleDismiss(false)}
        onDismissed={(event: CustomEvent<boolean>) => handleDismiss(event.detail)}>
        {snackbar.message}
        {snackbar.onUndo && (
          <ZinButton
            variant="link"
            slot="actions"
            className="zin-snackbar__action"
            onClick={() => {
              snackbar.onUndo?.()
              hideSnackbar()
            }}>
            Undo
          </ZinButton>
        )}
        {snackbar.actions?.map((action: SnackbarAction) => (
          <ZinButton
            key={[action.label, action.icon, action.variant].filter(Boolean).join('|')}
            variant={action.variant ?? 'link'}
            slot="actions"
            {...(action.icon ? { icon: action.icon } : {})}
            className="zin-snackbar__action"
            onClick={() => {
              action.onClick?.()
              hideSnackbar()
            }}>
            {action.label}
          </ZinButton>
        ))}
      </ZinSnackbar>
    </div>
  ) : null

  const themeRoot = typeof document !== 'undefined' ? document.getElementById('theme-root') : null

  return (
    <SnackbarContext.Provider value={{ showSnackbar, hideSnackbar }}>
      {children}
      {snackbarNode && (themeRoot ? createPortal(snackbarNode, themeRoot) : snackbarNode)}
    </SnackbarContext.Provider>
  )
}

export const useSnackbar = () => {
  const context = useContext(SnackbarContext)
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider')
  }
  return context
}
